import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Card, DatePicker, Table, Typography, Row, Col, Divider, Spin, Empty, Button, Avatar, Menu, Dropdown, message } from "antd";
import { UserOutlined, SolutionOutlined, CalendarOutlined, DownloadOutlined, FundProjectionScreenOutlined, RiseOutlined, FallOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { fetchLedgerWithAccountDetails } from "../../core/data/redux/payroll/Accounting/providentFund/pfLedger/pfLedgerAction";
import dayjs, { Dayjs } from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AppDispatch } from "../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { getEmployeeByUserId } from "../../core/data/redux/employee/employeeAction";
import { exportPfToPDF, exportToCSV, exportToExcel } from "../../core/common/GlobalFunctions/exportTableData";
import { useAppConfig } from "../../customhooks/useAppConfig";
import { formatCurrency } from "../../core/utils/configFormatters";

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface Transaction {
    key: string;
    date: string;
    description: string;
    credit: number;
    debit: number;
    balance: number;
}

const headerCardStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #6e45e2 0%, #88d3ce 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
};

const statCardBaseStyle: React.CSSProperties = {
    color: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
};

const totalCreditsCardStyle: React.CSSProperties = {
    ...statCardBaseStyle,
    backgroundColor: '#28a745',
};

const totalDebitsCardStyle: React.CSSProperties = {
    ...statCardBaseStyle,
    backgroundColor: '#dc3545',
};

const currentBalanceCardStyle: React.CSSProperties = {
    ...statCardBaseStyle,
    backgroundColor: '#007bff',
};

const PfPassbook: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { employeesbyUser } = useSelector((state: any) => state.employee);
    const { pfEmployeeTransaction } = useSelector((state: any) => state.providentFund.pfLedger);

    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
    const [pageSize, setPageSize] = useState(5);
    const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month').startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());


    // Fetch employee info
    useEffect(() => {
        if (user?.UserId) {
            dispatch(getEmployeeByUserId({ userId: user?.UserId }));
        }
    }, [dispatch, user?.UserId]);

    // Fetch ledger info

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchLedger = useCallback((start: Dayjs, end: Dayjs) => {
        if (employeesbyUser?.employeeId) {
            setLoading(true);
            dispatch(fetchLedgerWithAccountDetails({
                employeeId: employeesbyUser.employeeId,
                startDate: start.format("YYYY-MM-DD"),
                endDate: end.format("YYYY-MM-DD"),
            })).finally(() => setLoading(false));
        }
    }, [dispatch, employeesbyUser?.employeeId]);

    useEffect(() => {
        if (employeesbyUser?.employeeId) {
            fetchLedger(startDate, endDate);
        }
    }, [employeesbyUser?.employeeId, endDate, fetchLedger, startDate]);


    // Process ledger into transactions
    useEffect(() => {
        if (pfEmployeeTransaction?.ledgerTransactions?.length) {
            const sortedTransactions: Transaction[] = pfEmployeeTransaction.ledgerTransactions
                .map((txn: any, index: number) => {
                    const isCredit = txn.transactionType === "Contribution" || txn.transactionType === "Interest";
                    const isDebit = txn.transactionType === "Withdrawal" || txn.transactionType === "Loan";
                    return {
                        key: `${txn.pfLedgerId || index}`,
                        date: dayjs(txn.transactionDate).format("YYYY-MM-DD"),
                        description: txn.description,
                        credit: isCredit ? parseFloat(txn.amount) : 0,
                        debit: isDebit ? parseFloat(txn.amount) : 0,
                        balance: 0,
                    };
                })
                .sort((a: any, b: any) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

            // Calculate running balance
            let runningBalance = 0;
            const withBalance = sortedTransactions.map(txn => {
                runningBalance += txn.credit - txn.debit;
                return { ...txn, balance: runningBalance };
            });

            setTransactions(withBalance);
            setFilteredTransactions(withBalance);
        }
        setLoading(false);
    }, [pfEmployeeTransaction]);

    const overallStats = useMemo(() => {
        const totalCredit = transactions.reduce((sum, txn) => sum + txn.credit, 0);
        const totalDebit = transactions.reduce((sum, txn) => sum + txn.debit, 0);
        const currentBalance = transactions.length ? transactions[transactions.length - 1].balance : 0;
        return { totalCredit, totalDebit, currentBalance };
    }, [transactions]);

    const handleDateFilter = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
        if (!dates || !dates[0] || !dates[1]) {
            setFilteredTransactions(transactions);
            return;
        }

        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);

        fetchLedger(start, end);
    };
    const config = useAppConfig();



    const columns = [
        { title: "Date", dataIndex: "date", key: "date", render: (text: string) => dayjs(text).format("DD MMM, YYYY"), sorter: (a: Transaction, b: Transaction) => dayjs(a.date).diff(dayjs(b.date)), defaultSortOrder: 'ascend' as const },
        { title: "Description", dataIndex: "description", key: "description" },
        { title: "Credit (₹)", dataIndex: "credit", key: "credit", align: 'right' as const, render: (value: number) => (value > 0 ? <Text style={{ color: 'green', fontWeight: 'bold' }}>{formatCurrency(value, config)}</Text> : "-") },
        { title: "Debit (₹)", dataIndex: "debit", key: "debit", align: 'right' as const, render: (value: number) => (value > 0 ? <Text type="danger" strong>{formatCurrency(value, config)}</Text> : "-") },
        { title: "Balance (₹)", dataIndex: "balance", key: "balance", align: 'right' as const, render: (value: number) => <Text strong>{formatCurrency(value, config)}</Text> },
    ];

    if (loading) {
        return (
            <div className="page-wrapper content d-flex justify-content-center align-items-center" style={{ minHeight: '80vh', backgroundColor: '#f0f2f5' }}>
                <Spin size="large" tip="Loading Passbook..." />
            </div>
        );
    }
    const exportableData = filteredTransactions.map(txn => ({
        Date: dayjs(txn.date).format("DD-MM-YYYY"),
        Description: txn.description,
        Credit: txn.credit > 0 ? txn.credit : "",
        Debit: txn.debit > 0 ? txn.debit : "",
        Balance: txn.balance,
    }));
    const handleExport = (format: 'excel' | 'pdf' | 'csv') => {
        if (!exportableData || !Array.isArray(exportableData) || exportableData.length === 0) {
            message.error("No data available to export. Please check the data and try again.");
            return;
        }
        const fileName = `PF_Passbook_${dayjs().format("YYYYMMDD")}`;
        const empName = pfEmployeeTransaction?.pfEmployee?.Name ?? 'Employee Name';
        const pfNumber = pfEmployeeTransaction?.pfEmployee?.pfAccountNumber ?? 'N/A';

        switch (format) {
            case 'excel':
                exportToExcel(exportableData, fileName);
                break;
            case 'pdf':
                exportPfToPDF(exportableData, fileName, {
                    employeeName: empName,
                    pfNumber: pfNumber,
                });
                break;
            case 'csv':
                exportToCSV(exportableData, fileName);
                break;
            default:
                break;
        }
    };

    const exportMenu = (
        <Menu onClick={({ key }) => handleExport(key as 'excel' | 'pdf' | 'csv')}>
            <Menu.Item key="excel">Export to Excel</Menu.Item>
            <Menu.Item key="pdf">Export to PDF</Menu.Item>
            <Menu.Item key="csv">Export to CSV</Menu.Item>
        </Menu>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa' }}>
                    <div style={headerCardStyle} className="shadow">
                        <Row align="middle" gutter={[16, 16]}>
                            <Col xs={24} sm={4} md={3} lg={2} className="text-center text-sm-start">
                                <Avatar size={64} icon={<FundProjectionScreenOutlined />} style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
                            </Col>
                            <Col xs={24} sm={12} md={15} lg={16}>
                                <Title level={3} style={{ color: 'white', marginBottom: '4px' }}>Passbook</Title>
                                <Text style={{ color: 'rgba(255,255,255,0.85)', display: 'block' }}>
                                    <UserOutlined /> {pfEmployeeTransaction?.pfEmployee?.Name ?? "Employee Name"} <span className="mx-2">|</span> <SolutionOutlined /><span className="ms-2">Account: {pfEmployeeTransaction?.pfEmployee?.pfAccountNumber || "N/A"}</span>
                                </Text>
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6} className="text-center text-sm-end">
                                <Dropdown overlay={exportMenu} placement="bottomRight">
                                    <Button
                                        icon={<DownloadOutlined />}
                                        style={{ backgroundColor: '#20c997', borderColor: '#20c997', color: 'white' }}
                                    >
                                        Export
                                    </Button>
                                </Dropdown>
                            </Col>
                        </Row>
                    </div>

                    <Row gutter={[16, 16]} className="mt-4">
                        <Col xs={24} md={8}>
                            <div style={totalCreditsCardStyle} className="shadow-sm">
                                <Text style={{ fontSize: '0.9rem', display: 'block', opacity: 0.85 }}>Total Credits</Text>
                                <Title level={2} style={{ color: 'white', margin: '8px 0' }}>{formatCurrency(overallStats.totalCredit, config)}</Title>
                                <Text style={{ fontSize: '0.85rem', opacity: 0.9 }}><RiseOutlined /> Monthly contributions</Text>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div style={totalDebitsCardStyle} className="shadow-sm">
                                <Text style={{ fontSize: '0.9rem', display: 'block', opacity: 0.85 }}>Total Debits</Text>
                                <Title level={2} style={{ color: 'white', margin: '8px 0' }}>{formatCurrency(overallStats.totalDebit, config)}</Title>
                                <Text style={{ fontSize: '0.85rem', opacity: 0.9 }}><FallOutlined /> Withdrawals</Text>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div style={currentBalanceCardStyle} className="shadow-sm">
                                <Text style={{ fontSize: '0.9rem', display: 'block', opacity: 0.85 }}>Current Balance</Text>
                                <Title level={2} style={{ color: 'white', margin: '8px 0' }}>{formatCurrency(overallStats.currentBalance, config)}</Title>
                                <Text style={{ fontSize: '0.85rem', opacity: 0.9 }}><CheckCircleOutlined /> Available amount</Text>
                            </div>
                        </Col>
                    </Row>

                    <Card className="shadow-sm p-3 mt-4">
                        <Row justify="space-between" align="middle" className="mb-3">
                            <Col xs={24} md={12}>
                                <Title level={4} className="mb-0"><CalendarOutlined /> Transaction History</Title>
                            </Col>
                            <Col xs={24} md={12} className="text-md-end mt-4 mt-md-0">
                                <RangePicker
                                    onChange={handleDateFilter}
                                    format="DD-MM-YYYY"
                                    allowClear={false}
                                    style={{ minWidth: 280 }}
                                    value={[startDate, endDate]}
                                />
                            </Col>
                        </Row>
                        <Divider className="mt-5 pt-2 mb-2" />
                        <Table
                            className="table datanew dataTable no-footer"
                            columns={columns}
                            dataSource={filteredTransactions}
                            pagination={{
                                locale: { items_per_page: "" },
                                pageSize,
                                showSizeChanger: true,
                                pageSizeOptions: ['5', '10', '20'],
                                onShowSizeChange: (_, size) => setPageSize(size),
                            }}
                            bordered
                            scroll={{ x: 'max-content' }}
                            locale={{ emptyText: <Empty description="No transactions found for the selected period." /> }}
                            summary={(pageData) => {
                                if (!pageData.length) return null;
                                const credit = pageData.reduce((sum, t) => sum + t.credit, 0);
                                const debit = pageData.reduce((sum, t) => sum + t.debit, 0);
                                const lastBalance = pageData[pageData.length - 1].balance;
                                return (
                                    <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                        <Table.Summary.Cell index={0} colSpan={2}>
                                            Page Totals
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} align="right">
                                            <Text strong style={{ color: 'green' }}>₹{credit.toLocaleString()}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={3} align="right">
                                            <Text strong type="danger">₹{debit.toLocaleString()}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={4} align="right">
                                            <Text strong>₹{lastBalance.toLocaleString()}</Text>
                                            <Text style={{ fontSize: '0.8em', marginLeft: 4 }}>(Balance of last entry on page)</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                );
                            }}
                        />
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default PfPassbook;
